import { useEffect, useRef, useState } from 'react'

/**
 * Helper function to format time ago
 */
function getTimeAgo(timestamp) {
  const now = new Date()
  const past = new Date(timestamp)
  const diffMs = now - past
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSeconds < 60) return 'now'
  if (diffMinutes < 60) return `${diffMinutes}m`
  if (diffHours < 24) return `${diffHours}h`
  if (diffDays < 7) return `${diffDays}d`
  return past.toLocaleDateString()
}

/**
 * Get notification icon based on type
 */
function getNotificationIcon(type) {
  switch (type) {
    case 'friend_request':
    case 'friend':
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
        </svg>
      )
    case 'message':
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
        </svg>
      )
    case 'mention':
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 11-8 0 4 4 0 018 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"/>
        </svg>
      )
    case 'system':
    default:
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      )
  }
}

/**
 * Get icon color based on notification type
 */
function getIconColor(type) {
  switch (type) {
    case 'friend_request':
    case 'friend':
      return 'text-blue-400'
    case 'message':
      return 'text-indigo-400'
    case 'mention':
      return 'text-yellow-400'
    case 'system':
    default:
      return 'text-slate-400'
  }
}

/**
 * ProfileQuickBubble - Floating card anchored above profile dock
 *
 * Shows:
 * - Larger avatar + name + status input
 * - Recent notifications list (5 latest)
 * - "Full profile" button
 */
export default function ProfileQuickBubble({
  isOpen,
  onClose,
  profile,
  notifications = [],
  unreadCount = 0,
  onMarkAsRead,
  onMarkAllAsRead,
  onOpenFullProfile,
  onOpenSettings,
  onUpdateStatus,
  anchorRef,
}) {
  const bubbleRef = useRef(null)
  const statusInputRef = useRef(null)
  const [statusText, setStatusText] = useState(profile.custom_status || '')
  const [isEditingStatus, setIsEditingStatus] = useState(false)

  // Get 5 most recent notifications
  const recentNotifications = notifications.slice(0, 5)
  const hasUnread = unreadCount > 0

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event) => {
      if (
        bubbleRef.current &&
        !bubbleRef.current.contains(event.target) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target)
      ) {
        onClose()
      }
    }

    // Small delay to prevent immediate close
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose, anchorRef])

  // Focus first interactive element when opening
  useEffect(() => {
    if (isOpen && bubbleRef.current) {
      const firstFocusable = bubbleRef.current.querySelector('button, input, [tabindex]:not([tabindex="-1"])')
      firstFocusable?.focus()
    }
  }, [isOpen])

  // Handle status save
  const handleSaveStatus = () => {
    if (statusText !== profile.custom_status) {
      onUpdateStatus?.(statusText)
    }
    setIsEditingStatus(false)
  }

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      onMarkAsRead?.(notification.id)
    }
  }

  // Get status color
  const getStatusColor = () => {
    switch (profile.status) {
      case 'online': return 'bg-gjyl-success'
      case 'idle': return 'bg-gjyl-warning'
      case 'dnd': return 'bg-gjyl-danger'
      default: return 'bg-gjyl-slate-600'
    }
  }

  const getStatusLabel = () => {
    switch (profile.status) {
      case 'online': return 'Online'
      case 'idle': return 'Idle'
      case 'dnd': return 'Do Not Disturb'
      default: return 'Offline'
    }
  }

  if (!isOpen) return null

  return (
    <div
      ref={bubbleRef}
      role="dialog"
      aria-modal="true"
      aria-label="Profile menu"
      className="
        absolute bottom-full left-0 mb-3
        w-80
        bg-[#1E1F22] rounded-xl shadow-2xl
        border border-gjyl-slate-700
        overflow-hidden
        animate-scale-in
        z-50
      "
      style={{ transformOrigin: 'bottom left' }}
    >
      {/* Profile Header */}
      <div className="p-4 border-b border-gjyl-slate-700/50">
        <div className="flex items-start gap-3">
          {/* Larger Avatar */}
          <div className="relative flex-shrink-0">
            <div className="w-14 h-14 rounded-gjyl-lg overflow-hidden border-2 border-gjyl-slate-600">
              {profile.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt={profile.username}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gjyl-slate-700 flex items-center justify-center text-white font-semibold text-lg">
                  {profile.username?.substring(0, 2).toUpperCase() || 'U'}
                </div>
              )}
            </div>
            {/* Status Dot */}
            <div
              className={`
                absolute -bottom-0.5 -right-0.5
                w-4 h-4 rounded-full
                border-2 border-gjyl-slate-800
                ${getStatusColor()}
              `}
            />
          </div>

          {/* Name & Status */}
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold truncate">
              {profile.display_name || profile.username}
            </h3>
            <p className="text-xs text-slate-400 truncate">
              @{profile.username}
            </p>
            <div className="flex items-center gap-1.5 mt-1">
              <span className={`w-2 h-2 rounded-full ${getStatusColor()}`} />
              <span className="text-xs text-slate-400">{getStatusLabel()}</span>
            </div>
          </div>

          {/* Settings Button */}
          <button
            onClick={onOpenSettings}
            className="p-2 rounded-lg hover:bg-gjyl-slate-700/50 transition-colors text-slate-400 hover:text-white"
            title="Settings"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>

        {/* Status Input */}
        <div className="mt-3">
          {isEditingStatus ? (
            <div className="flex items-center gap-2">
              <input
                ref={statusInputRef}
                type="text"
                value={statusText}
                onChange={(e) => setStatusText(e.target.value.slice(0, 128))}
                placeholder="Set a status..."
                className="flex-1 px-3 py-1.5 text-sm bg-gjyl-slate-800 border border-gjyl-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-gjyl-indigo-500"
                maxLength={128}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveStatus()
                  if (e.key === 'Escape') setIsEditingStatus(false)
                }}
              />
              <button
                onClick={handleSaveStatus}
                className="p-1.5 rounded-lg bg-gjyl-indigo-500 hover:bg-gjyl-indigo-400 text-white transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsEditingStatus(true)}
              className="w-full px-3 py-1.5 text-sm text-left glass rounded-lg text-slate-400 hover:text-white hover:glass-strong transition-all"
            >
              {profile.custom_status || 'Set a status...'}
            </button>
          )}
          {isEditingStatus && (
            <p className="text-[10px] text-slate-500 mt-1 text-right">
              {statusText.length}/128
            </p>
          )}
        </div>
      </div>

      {/* Recent Notifications */}
      <div className="p-2">
        <div className="flex items-center justify-between px-2 py-1 mb-1">
          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
            Recent Notifications
          </h4>
          {hasUnread && (
            <button
              onClick={onMarkAllAsRead}
              className="text-[10px] text-gjyl-indigo-400 hover:text-gjyl-indigo-300 transition-colors"
            >
              Mark all read
            </button>
          )}
        </div>

        {recentNotifications.length === 0 ? (
          <div className="px-2 py-4 text-center">
            <p className="text-sm text-slate-500">No notifications</p>
          </div>
        ) : (
          <div className="space-y-0.5 max-h-[200px] overflow-y-auto scrollbar-gjyl">
            {recentNotifications.map((notification) => (
              <button
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`
                  w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left
                  transition-all duration-150
                  ${notification.read
                    ? 'hover:bg-gjyl-slate-700/30'
                    : 'bg-gjyl-indigo-500/10 hover:bg-gjyl-indigo-500/20'
                  }
                `}
              >
                {/* Icon */}
                <div className={`flex-shrink-0 ${getIconColor(notification.type)}`}>
                  {getNotificationIcon(notification.type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">
                    {notification.title}
                  </p>
                </div>

                {/* Time */}
                <span className="flex-shrink-0 text-[10px] text-slate-500">
                  {getTimeAgo(notification.created_at)}
                </span>

                {/* Unread indicator */}
                {!notification.read && (
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-500" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="p-2 pt-0 border-t border-gjyl-slate-700/50 mt-1">
        <button
          onClick={onOpenFullProfile}
          className="
            w-full px-4 py-2.5
            bg-gjyl-indigo-500/20 hover:bg-gjyl-indigo-500/30
            text-gjyl-indigo-400 hover:text-gjyl-indigo-300
            text-sm font-medium
            rounded-lg
            transition-all duration-200
            flex items-center justify-center gap-2
          "
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Full Profile
        </button>
      </div>

      {/* Arrow pointing down toward dock */}
      <div
        className="
          absolute -bottom-2 left-4
          w-4 h-4
          bg-gjyl-slate-800
          border-r border-b border-gjyl-slate-700
          transform rotate-45
        "
      />
    </div>
  )
}
